import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * AdminEditLeadModal
 * Popup editor for Tag Leads (admin) to edit site location, provider info, and notes
 * with a single PATCH to reduce RU-heavy inline edits.
 * 
 * Uses createPortal to render at document.body level to avoid z-index/overflow issues.
 */
const AdminEditLeadModal = ({ isOpen, inspection, backendUrl, onClose, onSaved }) => {
  const [form, setForm] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [expandedImage, setExpandedImage] = useState(null); // For full-size image view
  const [additionalImages, setAdditionalImages] = useState([]); // For new images
  
  // Regional officers for RD/ASM selection
  const [regionalOfficers, setRegionalOfficers] = useState([]);
  const [loadingOfficers, setLoadingOfficers] = useState(false);
  
  // User's RD/ASM from their user record (for inheritance)
  const [userAssignment, setUserAssignment] = useState({ rd_email: null, asm_email: null });
  
  // Smarty enrichment state
  const [smartyStatus, setSmartyStatus] = useState(null); // {enriched, status, cost, data_preview}
  const [smartyEnriching, setSmartyEnriching] = useState(false);
  const [showSmartyData, setShowSmartyData] = useState(false);
  
  // Track the current inspection ID to prevent form reset on same inspection
  const currentInspectionIdRef = useRef(null);
  
  // Fetch regional officers when modal opens
  useEffect(() => {
    if (isOpen && backendUrl && regionalOfficers.length === 0) {
      setLoadingOfficers(true);
      fetch(`${backendUrl}/api/admin/regional-officers`, {
        headers: { 'X-Local-Dev': 'true' }
      })
        .then(res => res.json())
        .then(data => {
          if (data.officers) {
            setRegionalOfficers(data.officers.filter(o => o.active !== false));
          }
        })
        .catch(err => console.error('Failed to load regional officers:', err))
        .finally(() => setLoadingOfficers(false));
    }
  }, [isOpen, backendUrl, regionalOfficers.length]);
  
  // 🔗 Fetch submitter's user record to get their RD/ASM assignment
  useEffect(() => {
    if (!isOpen || !backendUrl || !inspection?.submitted_by_email) {
      setUserAssignment({ rd_email: null, asm_email: null });
      return;
    }
    
    const email = inspection.submitted_by_email.toLowerCase().trim();
    
    fetch(`${backendUrl}/api/admin/users/stats`, {
      headers: { 'X-Local-Dev': 'true' }
    })
      .then(res => res.json())
      .then(data => {
        const user = (data.users || []).find(u => 
          (u.email || '').toLowerCase().trim() === email
        );
        if (user) {
          setUserAssignment({
            rd_email: user.regional_sales_leader_email || null,
            asm_email: user.area_sales_manager_email || null
          });
        } else {
          setUserAssignment({ rd_email: null, asm_email: null });
        }
      })
      .catch(err => {
        console.error('Failed to fetch user assignment:', err);
        setUserAssignment({ rd_email: null, asm_email: null });
      });
  }, [isOpen, backendUrl, inspection?.submitted_by_email]);

  const derivedBusinessPhone = useMemo(() => {
    if (!inspection) return '';
    // Prefer top-level normalized phone; fallback to gps_data business_phone
    if (inspection.business_phone) return inspection.business_phone;
    if (inspection.gps_data) {
      try {
        const gps = typeof inspection.gps_data === 'string' ? JSON.parse(inspection.gps_data) : inspection.gps_data;
        return gps?.business_phone || '';
      } catch (_) {
        return '';
      }
    }
    return '';
  }, [inspection]);

  useEffect(() => {
    if (isOpen && inspection) {
      // Get the inspection ID (support both MongoDB _id and UUID id)
      const newInspectionId = inspection._id || inspection.id;
      
      // CRITICAL: Only reset form when the inspection ID actually changes
      // This prevents form data loss when parent component re-renders
      if (currentInspectionIdRef.current !== newInspectionId) {
        currentInspectionIdRef.current = newInspectionId;
        console.log('📝 AdminEditLeadModal: Loading form for inspection:', newInspectionId);
        
        setForm({
          business_name: inspection.business_name || '',
          business_address: inspection.business_address || '',
          business_phone: derivedBusinessPhone || '',
          site_street_address: inspection.site_street_address || '',
          site_city: inspection.site_city || '',
          site_state: inspection.site_state || '',
          site_zip: inspection.site_zip || '',
          current_provider: inspection.current_provider || '',
          working_service_company_name: inspection.working_service_company_name || '',
          working_service_company_phone: inspection.working_service_company_phone || '',
          notes: inspection.notes || '',
          last_inspection_date: inspection.last_inspection_date || '',
          next_due_date: inspection.next_due_date || '',
          // Sales conversion tracking
          sales_status: inspection.sales_status || 'new',
          sales_value: inspection.sales_value || '',
          sales_notes: inspection.sales_notes || '',
          sales_lost_reason: inspection.sales_lost_reason || '',
          // Regional Vice President / Regional Director
          // Priority: 1) Inspection record, 2) User's assignment (inherited)
          regional_sales_leader_email: inspection.regional_sales_leader_email || userAssignment.rd_email || '',
          area_sales_manager_email: inspection.area_sales_manager_email || userAssignment.asm_email || ''
        });
        setAdditionalImages([]); // Reset on open
      }
    } else if (!isOpen) {
      // Reset tracking when modal closes
      currentInspectionIdRef.current = null;
    }
  }, [isOpen, inspection, derivedBusinessPhone]);

  // 🔗 Update RD/ASM fields when userAssignment loads (async after form init)
  useEffect(() => {
    if (!isOpen || !inspection) return;
    
    // Only update if inspection doesn't have RD/ASM set but user does
    const inspHasRD = !!inspection.regional_sales_leader_email;
    const inspHasASM = !!inspection.area_sales_manager_email;
    
    if (!inspHasRD && userAssignment.rd_email) {
      setForm(prev => ({
        ...prev,
        regional_sales_leader_email: prev.regional_sales_leader_email || userAssignment.rd_email
      }));
    }
    if (!inspHasASM && userAssignment.asm_email) {
      setForm(prev => ({
        ...prev,
        area_sales_manager_email: prev.area_sales_manager_email || userAssignment.asm_email
      }));
    }
  }, [isOpen, inspection, userAssignment]);

  if (!isOpen || !inspection) return null;

  // Support both _id (Mongo ObjectId) and id (UUID) formats
  const inspectionId = inspection._id || inspection.id;

  const patchWithRetry = async (updates) => {
    const url = `${backendUrl}/api/admin/inspections/${inspectionId}`;
    let lastText = '';
    for (let attempt = 0; attempt < 3; attempt++) {
      const token = localStorage.getItem('token');
      const res = await fetch(url, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify(updates)
      });
      if (res.ok) return await res.json();
      lastText = await res.text().catch(() => '');
      const lower = lastText.toLowerCase();
      const isRateLimited = res.status === 429 || lower.includes('requestratetoolarge') || lower.includes('code\":16500') || lower.includes('code: 16500');
      if (isRateLimited && attempt < 2) {
        const delayMs = 350 * (2 ** attempt);
        await new Promise((r) => setTimeout(r, delayMs));
        continue;
      }
      break;
    }
    throw new Error(lastText || 'Failed to save');
  };

  // 🆕 Smarty Enrichment Handler
  const enrichWithSmarty = async () => {
    const inspectionId = inspection._id || inspection.id;
    const adminEmail = localStorage.getItem('admin_email') || localStorage.getItem('session_email') || 'admin';
    
    if (!window.confirm('Enrich this lead with Smarty property data?\n\nThis will cost ~$0.005 per lookup.')) {
      return;
    }
    
    setSmartyEnriching(true);
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${backendUrl}/api/admin/smarty/enrich/${inspectionId}?triggered_by=${encodeURIComponent(adminEmail)}`, {
        method: 'POST',
        headers: { 
          'X-Local-Dev': 'true', 
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      
      const data = await res.json();
      
      if (!res.ok || !data.success) {
        throw new Error(data.detail || data.error || 'Enrichment failed');
      }
      
      const preview = data.data || {};
      alert(`✅ Property data retrieved!\n\n` +
        `Owner: ${preview.owner_name || 'Unknown'}\n` +
        `Building: ${preview.building_sqft || 'Unknown'} sqft\n` +
        `Stories: ${preview.stories || 'Unknown'}\n` +
        `Fire Sprinklers: ${preview.fire_sprinklers ? '✅ Yes' : '❌ No'}\n` +
        `Year Built: ${preview.year_built || 'Unknown'}\n\n` +
        `Cost: $${data.cost.toFixed(4)}`
      );
      
      // Update status
      setSmartyStatus({
        enriched: true,
        status: 'success',
        cost: data.cost,
        enriched_at: data.enriched_at,
        data_preview: {
          owner: preview.owner_name,
          building_sqft: preview.building_sqft,
          fire_sprinklers: preview.fire_sprinklers
        }
      });
      setShowSmartyData(true);
      
    } catch (err) {
      alert(`❌ Smarty enrichment failed:\n\n${err.message}\n\nRetry available in Activity log.`);
    } finally {
      setSmartyEnriching(false);
    }
  };

  const save = async ({ approve, reject } = { approve: false, reject: false }) => {
    setIsSaving(true);
    try {
      // Only send fields we intend to edit
      const updates = {
        business_name: form.business_name,
        business_address: form.business_address,
        business_phone: form.business_phone,
        site_street_address: form.site_street_address,
        site_city: form.site_city,
        site_state: form.site_state,
        site_zip: form.site_zip,
        current_provider: form.current_provider,
        working_service_company_name: form.working_service_company_name,
        working_service_company_phone: form.working_service_company_phone,
        notes: form.notes,
        last_inspection_date: form.last_inspection_date,
        next_due_date: form.next_due_date,
        // Sales conversion tracking
        sales_status: form.sales_status,
        sales_value: form.sales_value ? parseFloat(form.sales_value) : null,
        sales_notes: form.sales_notes,
        sales_lost_reason: form.sales_status === 'lost' ? form.sales_lost_reason : null,
        sales_updated_at: new Date().toISOString()
      };
      
      // Include additional images if any
      if (additionalImages.length > 0) {
        updates.additional_images = additionalImages;
      }

      const result = await patchWithRetry(updates);
      
      // Track final status
      let finalStatus = inspection.status;
      
      // If approve flag is set, approve the inspection
      if (approve) {
        const approveToken = localStorage.getItem('token');
        const approveRes = await fetch(`${backendUrl}/api/admin/inspections/${inspectionId}/approve`, {
          method: 'PUT',
          headers: { 
            'Content-Type': 'application/json', 
            'X-Local-Dev': 'true',
            ...(approveToken && { 'Authorization': `Bearer ${approveToken}` })
          }
        });
        if (!approveRes.ok) {
          const errorText = await approveRes.text().catch(() => 'Unknown error');
          throw new Error(`Approve failed: ${errorText}`);
        }
        finalStatus = 'approved';
        console.log('✅ Inspection approved successfully');
      }
      
      // If reject flag is set, reject the inspection
      if (reject) {
        const rejectToken = localStorage.getItem('token');
        const rejectRes = await fetch(`${backendUrl}/api/admin/inspections/${inspectionId}/reject`, {
          method: 'PUT',
          headers: { 
            'Content-Type': 'application/json', 
            'X-Local-Dev': 'true',
            ...(rejectToken && { 'Authorization': `Bearer ${rejectToken}` })
          },
          body: JSON.stringify({ reason: 'Rejected by admin' })
        });
        if (!rejectRes.ok) {
          const errorText = await rejectRes.text().catch(() => 'Unknown error');
          throw new Error(`Reject failed: ${errorText}`);
        }
        finalStatus = 'rejected';
        console.log('✅ Inspection rejected successfully');
      }
      
      const merged = { 
        ...inspection, 
        ...(result?.updates || updates), 
        status: finalStatus
      };
      if (onSaved) onSaved(merged, result);
      if (onClose) onClose();
    } catch (e) {
      console.error('❌ AdminEditLeadModal save failed:', e);
      alert(`Failed to save: ${e.message || e}`);
    } finally {
      setIsSaving(false);
    }
  };

  const modalContent = (
    <div 
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100000] flex items-center justify-center p-4 pt-20 pb-6"
      onClick={(e) => {
        // CRITICAL: Stop propagation to prevent React event bubbling through portal to parent components
        e.stopPropagation();
      }}
    >
      <div 
        className="bg-[var(--pbfs-navy)] rounded-2xl max-w-3xl w-full max-h-[calc(100vh-6rem)] overflow-hidden shadow-2xl border border-white/10 flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 sm:p-6 border-b border-white/10 flex items-center justify-between flex-shrink-0">
          <div>
            <h3 className="text-white text-xl font-bold">Edit Tag Lead</h3>
            <p className="text-white/60 text-sm">
              Site location, provider info, and notes (single save).
            </p>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white text-2xl leading-none" title="Close">
            ×
          </button>
        </div>

        <div className="p-4 sm:p-6 space-y-4 sm:space-y-5 overflow-y-auto flex-1 min-h-0">
          {/* Submitted By */}
          <div className="bg-white/5 border border-white/10 rounded-xl p-3">
            <div className="text-white/70 text-sm">Submitted By</div>
            <div className="text-white font-semibold mt-1">
              {inspection.submitted_by || 'Unknown'} 
              {inspection.submitted_by_email && <span className="text-white/60 text-sm ml-2">({inspection.submitted_by_email})</span>}
            </div>
          </div>
          
          {/* 👥 Regional Vice President / Regional Director Assignment */}
          <div className="p-3 bg-gradient-to-r from-[var(--pbfs-blue)]/10 to-transparent border border-[var(--pbfs-blue)]/30 rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-lg">👥</span>
              <label className="text-[var(--pbfs-blue)] text-sm font-bold">Sales Assignment</label>
              {loadingOfficers && <span className="text-white/40 text-xs">Loading...</span>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Regional Vice President */}
              <div>
                <label className="text-white/70 text-xs">Regional Vice President</label>
                <select
                  value={form.regional_sales_leader_email || ''}
                  onChange={(e) => setForm({ ...form, regional_sales_leader_email: e.target.value })}
                  className="w-full mt-1 px-3 py-2 bg-[#00314F] border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:border-[var(--pbfs-blue)]"
                  style={{ colorScheme: 'dark' }}
                  disabled={loadingOfficers}
                >
                  <option value="" className="bg-[#00314F] text-white">Unassigned</option>
                  {regionalOfficers
                    .filter(o => o.role === 'regional_sales_leader' || o.role === 'regional_director' || o.role === 'rd' || !o.role)
                    .map(officer => (
                      <option key={officer.id || officer.email} value={officer.email} className="bg-[#00314F] text-white">
                        {officer.name} {officer.region ? `(${officer.region})` : ''}
                      </option>
                    ))
                  }
                </select>
              </div>
              
              {/* Regional Director */}
              <div>
                <label className="text-white/70 text-xs">Regional Director</label>
                <select
                  value={form.area_sales_manager_email || ''}
                  onChange={(e) => setForm({ ...form, area_sales_manager_email: e.target.value })}
                  className="w-full mt-1 px-3 py-2 bg-[#00314F] border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:border-[var(--pbfs-blue)]"
                  style={{ colorScheme: 'dark' }}
                  disabled={loadingOfficers}
                >
                  <option value="" className="bg-[#00314F] text-white">Direct to RD</option>
                  {regionalOfficers
                    .filter(o => o.role === 'area_sales_manager' || o.role === 'asm')
                    .map(officer => (
                      <option key={officer.id || officer.email} value={officer.email} className="bg-[#00314F] text-white">
                        {officer.name} {officer.region ? `(${officer.region})` : ''}
                      </option>
                    ))
                  }
                </select>
                <div className="text-white/40 text-[10px] mt-1">
                  RD reports to Regional Vice President
                </div>
              </div>
            </div>
          </div>
          
          {/* Thumbnail + AI Dates + Additional Images */}
          <div className="bg-white/5 border border-white/10 rounded-xl p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Thumbnail - Clickable */}
              <div>
                <div className="text-white/70 text-sm mb-2 flex items-center justify-between">
                  <span>Tag Image</span>
                  <button
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = 'image/*';
                      input.onchange = (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onload = (event) => {
                            setAdditionalImages(prev => [...prev, {
                              data: event.target.result,
                              name: file.name,
                              timestamp: new Date().toISOString()
                            }]);
                          };
                          reader.readAsDataURL(file);
                        }
                      };
                      input.click();
                    }}
                    className="text-white/60 hover:text-[var(--pbfs-green)] transition-colors text-lg"
                    title="Add additional image"
                  >
                    📎
                  </button>
                </div>
                {inspection.image_url || inspectionId ? (
                  <img
                    src={inspection.image_url ? `${backendUrl}${inspection.image_url}` : `${backendUrl}/api/inspections/${inspectionId}/image?thumbnail=true`}
                    alt="Tag"
                    onClick={() => setExpandedImage(inspection.image_url ? `${backendUrl}${inspection.image_url}` : `${backendUrl}/api/inspections/${inspectionId}/image`)}
                    className="w-full h-32 object-cover rounded-lg border border-white/10 bg-black/20 cursor-pointer hover:border-[var(--pbfs-green)] transition-all"
                    loading="lazy"
                    title="Click to view full size"
                  />
                ) : (
                  <div className="w-full h-32 rounded-lg border border-white/10 bg-black/20 flex items-center justify-center text-white/40 text-xs">
                    No image
                  </div>
                )}
                {additionalImages.length > 0 && (
                  <div className="mt-2 text-[var(--pbfs-green)] text-xs font-semibold">
                    +{additionalImages.length} additional
                  </div>
                )}
              </div>
              
              {/* Last Inspection Date */}
              <div className="min-w-0 overflow-hidden">
                <label className="text-white/70 text-sm mb-2 block truncate">Last Inspection Date (AI)</label>
                <input
                  type="date"
                  value={(form.last_inspection_date || '').slice(0, 10)}
                  onChange={(e) => setForm({ ...form, last_inspection_date: e.target.value })}
                  className="w-full max-w-full px-2 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:border-[var(--pbfs-green)]"
                  style={{ colorScheme: 'dark' }}
                />
                <div className="text-white/40 text-xs mt-1">
                  Date captured from tag
                </div>
              </div>
              
              {/* Next Due Date */}
              <div className="min-w-0 overflow-hidden">
                <label className="text-white/70 text-sm mb-2 block truncate">Next Due Date (AI)</label>
                <input
                  type="date"
                  value={(form.next_due_date || '').slice(0, 10)}
                  onChange={(e) => setForm({ ...form, next_due_date: e.target.value })}
                  className="w-full max-w-full px-2 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:border-[var(--pbfs-green)]"
                  style={{ colorScheme: 'dark' }}
                />
                <div className="text-white/40 text-xs mt-1">
                  Calculated expiration
                </div>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-white/70 text-sm">Business Name</label>
              <input
                value={form.business_name || ''}
                onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                className="w-full mt-1 px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-[var(--pbfs-green)]"
              />
            </div>
            <div>
              <label className="text-white/70 text-sm">Business Phone</label>
              <input
                value={form.business_phone || ''}
                onChange={(e) => setForm({ ...form, business_phone: e.target.value })}
                className="w-full mt-1 px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-[var(--pbfs-green)]"
                placeholder="(555) 123-4567"
              />
            </div>
          </div>

          <div>
            <label className="text-white/70 text-sm">Business Address (optional)</label>
            <input
              value={form.business_address || ''}
              onChange={(e) => setForm({ ...form, business_address: e.target.value })}
              className="w-full mt-1 px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-[var(--pbfs-green)]"
            />
          </div>

          <div>
            <label className="text-white/70 text-sm">Site Location</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-1">
              <input
                value={form.site_street_address || ''}
                onChange={(e) => setForm({ ...form, site_street_address: e.target.value })}
                className="px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-[var(--pbfs-green)]"
                placeholder="Street"
              />
              <input
                value={form.site_city || ''}
                onChange={(e) => setForm({ ...form, site_city: e.target.value })}
                className="px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-[var(--pbfs-green)]"
                placeholder="City"
              />
              <input
                value={form.site_state || ''}
                onChange={(e) => setForm({ ...form, site_state: e.target.value })}
                className="px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-[var(--pbfs-green)]"
                placeholder="State"
              />
              <input
                value={form.site_zip || ''}
                onChange={(e) => setForm({ ...form, site_zip: e.target.value })}
                className="px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-[var(--pbfs-green)]"
                placeholder="ZIP"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-white/70 text-sm">Current Provider (AI)</label>
              <input
                value={form.current_provider || ''}
                onChange={(e) => setForm({ ...form, current_provider: e.target.value })}
                className="w-full mt-1 px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-[var(--pbfs-green)]"
                placeholder="Provider name"
              />
            </div>
            <div>
              <label className="text-white/70 text-sm">Provider Phone (override)</label>
              <input
                value={form.working_service_company_phone || ''}
                onChange={(e) => setForm({ ...form, working_service_company_phone: e.target.value })}
                className="w-full mt-1 px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-[var(--pbfs-green)]"
                placeholder="(555) 123-4567"
              />
            </div>
          </div>

          <div>
            <label className="text-white/70 text-sm">Provider Name (override)</label>
            <input
              value={form.working_service_company_name || ''}
              onChange={(e) => setForm({ ...form, working_service_company_name: e.target.value })}
              className="w-full mt-1 px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-[var(--pbfs-green)]"
              placeholder="Override provider name (optional)"
            />
          </div>

          {/* 💰 Sales Conversion Tracking */}
          <div className="p-3 bg-gradient-to-r from-[var(--pbfs-green)]/10 to-transparent border border-[var(--pbfs-green)]/30 rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-lg">💰</span>
              <label className="text-[var(--pbfs-green)] text-sm font-bold">Sales Conversion</label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-white/70 text-xs">Status</label>
                <select
                  value={form.sales_status || 'new'}
                  onChange={(e) => setForm({ ...form, sales_status: e.target.value })}
                  className="w-full mt-1 px-3 py-2 bg-[#00314F] border border-white/20 rounded-lg text-white focus:outline-none focus:border-[var(--pbfs-green)]"
                  style={{ colorScheme: 'dark' }}
                >
                  <option value="new" className="bg-[#00314F] text-white">New</option>
                  <option value="distributed" className="bg-[#00314F] text-white">Distributed</option>
                  <option value="contacted" className="bg-[#00314F] text-white">Contacted</option>
                  <option value="quoting" className="bg-[#00314F] text-white">Quoting</option>
                  <option value="won" className="bg-[#00314F] text-white">Won ✓</option>
                  <option value="lost" className="bg-[#00314F] text-white">Lost ✗</option>
                </select>
              </div>
              <div>
                <label className="text-white/70 text-xs">Deal Value ($)</label>
                <input
                  type="number"
                  value={form.sales_value || ''}
                  onChange={(e) => setForm({ ...form, sales_value: e.target.value })}
                  className="w-full mt-1 px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-[var(--pbfs-green)]"
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
            {form.sales_status === 'lost' && (
              <div className="mt-3">
                <label className="text-white/70 text-xs">Lost Reason</label>
                <select
                  value={form.sales_lost_reason || ''}
                  onChange={(e) => setForm({ ...form, sales_lost_reason: e.target.value })}
                  className="w-full mt-1 px-3 py-2 bg-[#00314F] border border-white/20 rounded-lg text-white focus:outline-none focus:border-[var(--pbfs-green)]"
                  style={{ colorScheme: 'dark' }}
                >
                  <option value="" className="bg-[#00314F] text-white">Select reason...</option>
                  <option value="price" className="bg-[#00314F] text-white">Price too high</option>
                  <option value="competitor" className="bg-[#00314F] text-white">Went with competitor</option>
                  <option value="no_response" className="bg-[#00314F] text-white">No response</option>
                  <option value="not_interested" className="bg-[#00314F] text-white">Not interested</option>
                  <option value="wrong_contact" className="bg-[#00314F] text-white">Wrong contact info</option>
                  <option value="other" className="bg-[#00314F] text-white">Other</option>
                </select>
              </div>
            )}
            <div className="mt-3">
              <label className="text-white/70 text-xs">Sales Notes</label>
              <input
                value={form.sales_notes || ''}
                onChange={(e) => setForm({ ...form, sales_notes: e.target.value })}
                className="w-full mt-1 px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-[var(--pbfs-green)]"
                placeholder="Notes about this lead..."
              />
            </div>
          </div>

          {/* 🏢 Smarty Property Data (if enriched) */}
          {smartyStatus?.enriched && smartyStatus.data_preview && (
            <div className="p-3 bg-gradient-to-r from-[var(--pbfs-blue)]/10 to-transparent border border-[var(--pbfs-blue)]/30 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-lg">🏢</span>
                  <label className="text-[var(--pbfs-blue)] text-sm font-bold">Property Data (Smarty)</label>
                </div>
                <span className="text-white/40 text-xs">
                  Enriched {smartyStatus.enriched_at ? new Date(smartyStatus.enriched_at).toLocaleDateString() : ''}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                {smartyStatus.data_preview.owner && (
                  <div className="col-span-2">
                    <span className="text-white/60">Owner:</span>
                    <div className="text-white font-semibold">{smartyStatus.data_preview.owner}</div>
                  </div>
                )}
                {smartyStatus.data_preview.building_sqft && (
                  <div>
                    <span className="text-white/60">Building:</span>
                    <div className="text-white font-semibold">{smartyStatus.data_preview.building_sqft.toLocaleString()} sqft</div>
                  </div>
                )}
                {smartyStatus.data_preview.fire_sprinklers !== undefined && (
                  <div>
                    <span className="text-white/60">Fire Sprinklers:</span>
                    <div className={`font-semibold ${smartyStatus.data_preview.fire_sprinklers ? 'text-green-400' : 'text-red-400'}`}>
                      {smartyStatus.data_preview.fire_sprinklers ? '✅ Yes' : '❌ No'}
                    </div>
                  </div>
                )}
              </div>
              <button
                onClick={() => setShowSmartyData(!showSmartyData)}
                className="mt-3 text-[var(--pbfs-blue)] text-xs underline hover:text-white transition-colors"
              >
                {showSmartyData ? '▼ Hide' : '▶ View'} full property data
              </button>
              {showSmartyData && inspection?.smarty_data && (
                <pre className="mt-3 p-3 bg-black/30 rounded-lg text-white/70 text-[10px] overflow-x-auto max-h-60 overflow-y-auto">
                  {JSON.stringify(inspection.smarty_data, null, 2)}
                </pre>
              )}
            </div>
          )}

          <div>
            <label className="text-white/70 text-sm">Notes</label>
            <textarea
              value={form.notes || ''}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={4}
              className="w-full mt-1 px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-[var(--pbfs-green)] resize-none"
              placeholder="Add notes..."
            />
            <div className="mt-2 text-white/40 text-xs">
              Voice/additional note sources stay visible in the table; this field edits the primary notes value.
            </div>
          </div>
        </div>

        {/* Footer - Compact button layout for mobile */}
        <div className="px-3 sm:px-4 py-3 sm:py-4 border-t border-white/10 flex items-stretch gap-2 flex-shrink-0 bg-[var(--pbfs-navy)]">
          {/* Cancel - Compact icon button */}
          <button
            onClick={onClose}
            disabled={isSaving}
            className="w-12 sm:w-14 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white text-xl rounded-lg transition-all disabled:opacity-50"
            title="Cancel"
          >
            ✕
          </button>
          
          {/* 🆕 Smarty Enrichment - Only show for approved leads */}
          {(inspection?.status === 'approved' || inspection?.approval_status === 'approved') && (
            <button
              onClick={enrichWithSmarty}
              disabled={isSaving || smartyEnriching}
              className={`px-3 py-2 rounded-lg transition-all font-semibold text-xs sm:text-sm flex items-center gap-1.5 ${
                smartyStatus?.enriched
                  ? 'bg-[var(--pbfs-blue)]/20 border border-[var(--pbfs-blue)]/40 text-[var(--pbfs-blue)] hover:bg-[var(--pbfs-blue)]/30'
                  : 'bg-yellow-500/20 border border-yellow-500/40 text-yellow-400 hover:bg-yellow-500/30 animate-pulse'
              } disabled:opacity-50`}
              title={smartyStatus?.enriched ? `Enriched ${smartyStatus.enriched_at ? new Date(smartyStatus.enriched_at).toLocaleDateString() : ''}` : 'Enrich with property data (~$0.005)'}
            >
              <span>{smartyEnriching ? '⏳' : smartyStatus?.enriched ? '✓' : '🏢'}</span>
              <span className="hidden sm:inline">{smartyEnriching ? 'Enriching...' : smartyStatus?.enriched ? 'Enriched' : 'Smarty'}</span>
            </button>
          )}
          
          {/* Approve - Primary action (green) */}
          <button
            onClick={() => save({ approve: true, reject: false })}
            disabled={isSaving}
            className="flex-1 py-3 px-3 sm:px-4 bg-[var(--pbfs-green)] hover:bg-[var(--pbfs-green)]/90 text-white font-bold rounded-lg transition-all disabled:opacity-50 flex items-center justify-center gap-1.5 active:scale-[0.98]"
          >
            <span className="text-lg">✓</span>
            <span className="text-sm sm:text-base">{isSaving ? 'Saving…' : 'Approve'}</span>
          </button>
          
          {/* Reject - Destructive action (red) */}
          <button
            onClick={() => save({ approve: false, reject: true })}
            disabled={isSaving}
            className="flex-1 py-3 px-3 sm:px-4 bg-red-500 hover:bg-red-600 text-white font-bold rounded-lg transition-all disabled:opacity-50 flex items-center justify-center gap-1.5 active:scale-[0.98]"
          >
            <span className="text-lg">✗</span>
            <span className="text-sm sm:text-base">{isSaving ? 'Saving…' : 'Reject'}</span>
          </button>
        </div>
      </div>
      
      {/* Expanded Image Modal */}
      {expandedImage && (
        <div
          className="fixed inset-0 bg-black/95 backdrop-blur-sm z-[100001] flex items-center justify-center p-4"
          onClick={() => setExpandedImage(null)}
        >
          <div className="relative max-w-full max-h-full flex items-center justify-center">
            <img
              src={expandedImage}
              alt="Full size tag"
              className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            {/* Close button - Always visible on image */}
            <button
              onClick={() => setExpandedImage(null)}
              className="absolute top-3 right-3 w-12 h-12 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-2xl font-bold shadow-2xl transition-all hover:scale-110 active:scale-95"
              title="Close"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );

  // Render via portal to escape any parent overflow/z-index constraints
  return createPortal(modalContent, document.body);
};

export default AdminEditLeadModal;


